/**
 * The Quality tab's small parts: the status pill, a state box (loading, empty, error), the check icon, the trend
 * sparkline, the score ring, the banner (icon, text, actions on the right, ×), the head of a sub-view (its title the
 * workspace's last crumb, its status and tools on one row), and the "Add an ignore rule" form (Settings and a
 * check's findings use it).
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, X } from '@phosphor-icons/react';
import { IGNORE_CHECKS, checkTitle, dateIn, ringDash, scoreTone, statusOf, validateIgnore } from './qualityModel';


/**
 * Brings `element` to the top of the Quality panel by scrolling the panel only (2026-10-31: scrollIntoView also scrolled
 * the page around it, which pushed the tab row and title out of view and left a blank strip at the bottom). Its
 * scroll-margin-top (the sticky header's height) is kept. Outside a panel, or where scrollTo is missing: scrollIntoView.
 */
export function scrollInPanel(element, { smooth = true } = {}) {
  if (!element) { return; }
  const panel = element.closest?.('.quality-panel');
  const behavior = smooth ? 'smooth' : 'auto';
  if (!panel || typeof panel.scrollTo !== 'function') {
    element.scrollIntoView?.({ behavior, block: 'start' });
    return;
  }
  const margin = parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0;
  const top = element.getBoundingClientRect().top - panel.getBoundingClientRect().top + panel.scrollTop - margin;
  panel.scrollTo({ top: Math.max(0, top), behavior });
}

export function Pill({ tone = 'info', children, className = '', ...rest }) {
  return <span className={`quality-pill is-${tone}${className ? ` ${className}` : ''}`} {...rest}>{children}</span>;
}

Pill.propTypes = { children: PropTypes.node, className: PropTypes.string, tone: PropTypes.string };

export function StatusPill({ status, ...rest }) {
  const s = statusOf(status);
  return <Pill tone={s.tone} {...rest}>{s.label}</Pill>;
}

StatusPill.propTypes = { status: PropTypes.string };

export function State({ title, children = null, tone = null, action = null, testId = undefined }) {
  return (
    <div className={`quality-state${tone ? ` is-${tone}` : ''}`} data-testid={testId} role={tone === 'bad' ? 'alert' : undefined}>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

State.propTypes = { action: PropTypes.node, children: PropTypes.node, testId: PropTypes.string, title: PropTypes.node.isRequired, tone: PropTypes.string };

export function CheckIcon({ path }) {
  return (
    <span aria-hidden className="quality-icon">
      <svg fill="none" height="16" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 16 16" width="16"><path d={path} /></svg>
    </span>
  );
}

CheckIcon.propTypes = { path: PropTypes.string.isRequired };

export function Sparkline({ points, tone = 'info', label }) {
  if (!points) { return <span aria-hidden className="quality-spark is-empty" />; }
  return (
    <svg aria-label={label} className={`quality-spark is-${tone}`} height="30" role="img" viewBox="0 0 96 30" width="96">
      <polyline fill="none" points={points} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

Sparkline.propTypes = { label: PropTypes.string, points: PropTypes.string, tone: PropTypes.string };

export function ScoreRing({ score, size = 104, label = 'Quality score', tone = null }) {
  const none = score === null || score === undefined;
  return (
    <svg aria-label={none ? `No ${label.toLowerCase()} yet` : `${label} ${score} out of 100`} className={`quality-ring is-${tone || scoreTone(score)}`} height={size} role="img" viewBox="0 0 80 80" width={size}>
      <circle className="quality-ring-track" cx="40" cy="40" fill="none" r="34" strokeWidth="8" />
      {!none && <circle className="quality-ring-arc" cx="40" cy="40" fill="none" r="34" strokeDasharray={ringDash(score)} strokeLinecap="round" strokeWidth="8" transform="rotate(-90 40 40)" />}
      <text className="quality-ring-text" fontSize="20" fontWeight="700" textAnchor="middle" x="40" y="47">{none ? '–' : score}</text>
    </svg>
  );
}

ScoreRing.propTypes = {
  // What the score is, for screen readers ("GDPR score 19 out of 100").
  label: PropTypes.string,
  score: PropTypes.number,
  // Its width and height in px.
  size: PropTypes.number,
  // The arc's tone (pass, warn, fail, info); default: the Quality score's.
  tone: PropTypes.string,
};

/**
 * A banner of the Quality tab, one layout for all of them: the icon on the left, the text (it grows), the actions on
 * the right (real buttons), and × to dismiss when `onDismiss` is given. `tone`: info, warn, fail.
 */
export function Banner({
  tone = 'info', icon = null, children, actions = null, onDismiss = null, dismissLabel = 'Dismiss', role = 'status', testId = undefined, className = '',
}) {
  return (
    <div className={`quality-banner is-${tone} is-row${className ? ` ${className}` : ''}`} data-testid={testId} role={role}>
      {icon && <span aria-hidden className="quality-banner-icon">{icon}</span>}
      <div className="quality-banner-body">{children}</div>
      {actions && <div className="quality-banner-actions">{actions}</div>}
      {onDismiss && (
        <button aria-label={dismissLabel} className="quality-banner-close" onClick={onDismiss} title={dismissLabel} type="button">
          <X aria-hidden size={14} />
        </button>
      )}
    </div>
  );
}

Banner.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  dismissLabel: PropTypes.string,
  icon: PropTypes.node,
  onDismiss: PropTypes.func,
  role: PropTypes.string,
  testId: PropTypes.string,
  tone: PropTypes.string,
};

/**
 * Why the checks did not start, next to the button that was pressed (QualityPanel `runNote`): Run all checks (a small
 * popover under it, `pop`), a card's Run, a step's button, Retry, a check page's Run. Dismissable; the next click
 * replaces it.
 */
export function RunNote({ note, onDismiss, pop = false }) {
  if (!note) { return null; }
  return (
    <Banner className={`quality-run-note${pop ? ' is-pop' : ''}`} onDismiss={onDismiss} role="alert" testId="quality-run-note" tone="warn">
      <span>{note.text}</span>
    </Banner>
  );
}

RunNote.propTypes = { note: PropTypes.object, onDismiss: PropTypes.func.isRequired, pop: PropTypes.bool };

/**
 * Where a sub-view's title goes: in the workspace (QualityPanel's `onSubPage`) the tab's title line, as its last crumb
 * ("<Project> / Verify / Quality / API contract", Quality a link back to the overview): `(crumb | null) => void`.
 */
export const QualitySubPageContext = createContext(null);

/**
 * The head of a sub-view, one row. In the workspace its title is the title line's last crumb (QualitySubPageContext)
 * and the row keeps its own pills, last run and tools, on the right (`children`; none: no row). Elsewhere (the views
 * alone) "← Quality" (back to the overview), the title, then the same. `hint`: the title's tooltip; `mono`: a path.
 */
export function SubHead({ title, onBack, children = null, hint = null, mono = false }) {
  const toCrumb = useContext(QualitySubPageContext);
  const back = useRef(onBack);
  useEffect(() => { back.current = onBack; }, [onBack]);
  useEffect(() => {
    if (!toCrumb) { return undefined; }
    toCrumb({ title, hint, mono, onUp: () => back.current() });
    return () => toCrumb(null);
  }, [toCrumb, title, hint, mono]);
  if (toCrumb) {
    return children ? <div className="quality-head is-sub is-bare" data-testid="quality-subhead">{children}</div> : null;
  }
  return (
    <div className="quality-head is-sub" data-testid="quality-subhead">
      <nav aria-label="Quality breadcrumb" className="quality-crumb">
        <button className="quality-link is-quiet quality-back" onClick={onBack} title="Back to the Quality overview" type="button">
          <ArrowLeft aria-hidden size={14} />Quality
        </button>
      </nav>
      <h1 className={mono ? 'quality-mono' : undefined} title={hint || undefined}>{title}</h1>
      {children}
    </div>
  );
}

SubHead.propTypes = {
  children: PropTypes.node, hint: PropTypes.string, mono: PropTypes.bool, onBack: PropTypes.func.isRequired, title: PropTypes.string.isRequired,
};

const UNTIL_CHOICES = [
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
  { id: 'date', label: 'A date' },
  { id: 'never', label: 'No end (secrets only)' },
];

/**
 * Add an ignore rule: what (a rule, an advisory id, a package or a finding id), the check, a path glob, the reason
 * (required) and until (30 or 90 days, a date; no end only for secrets). `initial` prefills it (Ignore… on a finding).
 * `onSave(rule)` resolves, or rejects with a DataError whose 400 errors are shown by field.
 */
export function IgnoreForm({ initial = {}, onSave, onCancel = null, choices = [] }) {
  const [form, setForm] = useState({ what: initial.what || '', check: initial.check || '', path: initial.path || '', reason: '', untilMode: '30', date: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const untilOf = (f) => {
    if (f.untilMode === 'never') { return null; }
    if (f.untilMode === 'date') { return f.date || ''; }
    return dateIn(Number(f.untilMode));
  };

  const submit = async (event) => {
    event.preventDefault();
    const rule = { what: form.what.trim(), check: form.check || undefined, path: form.path.trim() || undefined, reason: form.reason.trim(), until: untilOf(form) };
    const found = validateIgnore({ ...rule, until: rule.until === '' ? '' : rule.until, check: form.check });
    if (form.untilMode === 'date' && !form.date) { found.until = 'Pick a date.'; }
    setErrors(found);
    if (Object.keys(found).length) { return; }
    setSaving(true);
    setFailure(null);
    try {
      await onSave(rule);
    } catch (error) {
      const byPath = Object.fromEntries((error?.body?.errors || []).map((e) => [String(e.path || '').replace(/^ignore(\[\d+\]|\.\d+)?\./, ''), e.message]));
      setErrors(byPath);
      setFailure(Object.keys(byPath).length ? null : (error?.body?.error || error?.message || 'Could not add the rule'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, input, wide = false) => (
    <span className={`quality-field${wide ? ' is-wide' : ''}`}>
      <label className="quality-field-label">
        <span>{label}</span>
        {input}
      </label>
      {errors[key] && <span className="quality-field-error" role="alert">{errors[key]}</span>}
    </span>
  );

  return (
    <form aria-label="Add an ignore rule" className="quality-ignore-form" data-testid="quality-ignore-form" noValidate onSubmit={submit}>
      {field('what', 'What', choices.length ? (
        <>
          <input aria-invalid={Boolean(errors.what)} className="quality-input quality-mono" list="quality-ignore-what" onChange={set('what')} placeholder="CVE-2023-37466, eval, a package…" value={form.what} />
          <datalist id="quality-ignore-what">{choices.map((c) => <option key={c} value={c} />)}</datalist>
        </>
      ) : <input aria-invalid={Boolean(errors.what)} className="quality-input quality-mono" onChange={set('what')} placeholder="CVE-2023-37466, eval, a package…" value={form.what} />)}
      {field('check', 'Check', (
        <select className="quality-input" onChange={set('check')} value={form.check}>
          <option value="">Any check</option>
          {IGNORE_CHECKS.map((id) => <option key={id} value={id}>{checkTitle(id)}</option>)}
        </select>
      ))}
      {field('path', 'Path (optional)', <input className="quality-input quality-mono" onChange={set('path')} placeholder="services/web/**" value={form.path} />)}
      {field('reason', 'Reason (required)', <input aria-invalid={Boolean(errors.reason)} className="quality-input" onChange={set('reason')} placeholder="Why this is safe for now" value={form.reason} />, true)}
      {field('until', 'Until', (
        <span className="quality-until">
          <select className="quality-input" onChange={set('untilMode')} value={form.untilMode}>
            {UNTIL_CHOICES.filter((c) => c.id !== 'never' || form.check === 'secrets').map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          {form.untilMode === 'date' && <input aria-label="End date" className="quality-input" onChange={set('date')} type="date" value={form.date} />}
        </span>
      ))}
      <span className="quality-form-actions">
        {onCancel && <button className="quality-btn" onClick={onCancel} type="button">Cancel</button>}
        <button className="quality-btn is-primary" disabled={saving} type="submit">{saving ? 'Saving…' : 'Save rule'}</button>
      </span>
      {failure && <p className="quality-field-error" role="alert">{failure}</p>}
    </form>
  );
}

IgnoreForm.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.string),
  initial: PropTypes.shape({ check: PropTypes.string, path: PropTypes.string, what: PropTypes.string }),
  onCancel: PropTypes.func,
  onSave: PropTypes.func.isRequired,
};
